import { createAction } from '@reduxjs/toolkit';
import type { ConvertExitCurrenciesRequest } from '@shared/schemas';

import type { CleaningTypeFilter } from '../domain/profile-cleaning.model';

/**
 * "Signal" actions: intents the UI dispatches for the saga to act on. Kept out of
 * the slice's reducers: they trigger side effects (load / clean / merge),
 * not the state transitions (the saga owns the resulting lifecycle + notice ones).
 */

/** Load both lists (dispatched by the page on mount). */
export const loadRequested = createAction('profileCleaning/loadRequested');

/**
 * Re-read both lists without the first-mount spinner: a profile was locked or
 * unlocked from cleaning (#702), which moves it in or out of every clean.
 */
export const refreshRequested = createAction('profileCleaning/refreshRequested');

/**
 * Clean every outstanding row of the given scope: `'all'` deletes all broken-URL
 * profiles *and* merges all duplicate groups, a single type restricts the run to
 * that type's endpoint. The scope is the page's active type filter; what is on
 * screen is what gets cleaned (#431).
 */
export const cleanRequested = createAction<{ type: CleaningTypeFilter }>(
  'profileCleaning/cleanRequested',
);

/**
 * Delete / merge only the ticked rows. Carries the raw ids rather than a prepared
 * `CleanPlan`: the saga re-derives the plan from the rows currently in the store,
 * for the same reason the backend re-queries; the list may have moved on since
 * the checkbox was ticked, and a plan frozen at tick time would act on it anyway.
 */
export const cleanSelectedRequested = createAction<{ ids: string[] }>(
  'profileCleaning/cleanSelectedRequested',
);

/** Merge a single duplicate group inline (the per-row action). */
export const mergeGroupRequested = createAction<{
  canonicalId: string;
  duplicateIds: string[];
  groupKey: string;
}>('profileCleaning/mergeGroupRequested');

/**
 * Read the sidebar badge's count (#753): dispatched by the SideNav on every admin
 * page change, served from the server's 5-minute cache (#759), and by the saga
 * once the page's rows are loaded or re-read, with `fresh: true` so the badge is
 * counted again and equals the page's "All issues" tile. The same answer carries
 * the badge of Exit & Investment Review (#762), heard through the shared
 * `issueCountReceived`.
 */
export const issueCountRequested = createAction(
  'profileCleaning/issueCountRequested',
  (payload: { fresh?: boolean } = {}) => ({ payload }),
);

/** Save the "Convert currencies" dialog of a mixed-currency profile (#732). */
export const convertRequested = createAction<{
  profileId: string;
  request: ConvertExitCurrenciesRequest;
}>('profileCleaning/convertRequested');
